import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';
import displayError from '../../utils/ErrorHandler';

const UpdateCommitments = () => {
  const [commitments, setCommitments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [formStates, setFormStates] = useState({});
  const [fileStates, setFileStates] = useState({});
  const [errors, setErrors] = useState({});
  const [messages, setMessages] = useState({});
  const [loading, setLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    };
    init();
  }, [user]);

  const fetchCommitments = async () => {
    if (!user?.Mauze) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/commit/get?jamiat=${user.Mauze}`,
        { withCredentials: true }
      );
      const data = res.data.data;

      const parsed = data.map((raw) => ({
        _id: raw._id,
        quantity: raw.quantity,
        contactName: raw.contactName,
        contactEmail: raw.contactEmail,
        contactNumber: raw.contactNumber,
        estimatedDeliveryDate: raw.estimatedDeliveryDate?.substring(0, 10) || '',
        status: raw.status,
        ewayBill: raw.ewayBill || '',
        silatItemName: raw.silatItem?.name,
        silatItemUnit: raw.silatItem?.unit,
        jamiatName: raw.jamiat?.jamiat_name,
      }));

      setCommitments(parsed);
      const initialStates = {};
      parsed.forEach((c) => (initialStates[c._id] = { ...c }));
      setFormStates(initialStates);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCommitments();
  }, [user]);

  const validateField = (name, value) => {
    switch (name) {
      case 'quantity':
        return !value || isNaN(value) || Number(value) <= 0 ? 'Quantity must be a positive number.' : '';
      case 'contactName':
        return !value?.trim() ? 'Contact name is required.' : '';
      case 'contactEmail':
        return !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value) ? 'Invalid email format.' : '';
      case 'contactNumber':
        return !/^\d{10,}$/.test(value) ? 'Contact number must be at least 10 digits.' : '';
      case 'estimatedDeliveryDate':
        return !value ? 'Estimated delivery date is required.' : '';
      default:
        return '';
    }
  };

  const handleInputChange = (id, name, value) => {
    setFormStates((prev) => ({
      ...prev,
      [id]: { ...prev[id], [name]: value },
    }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [name]: error },
    }));
  };

  const handleFileChange = (id, file) => {
    setFileStates((prev) => ({ ...prev, [id]: file }));
    setErrors((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), billFile: '' } }));
  };

  const handleSubmit = async (id) => {
    const formData = formStates[id];
    const requiredFields = ['quantity', 'contactName', 'contactEmail', 'contactNumber', 'estimatedDeliveryDate'];
    const newErrors = {};

    requiredFields.forEach((field) => {
      const err = validateField(field, formData[field]);
      if (err) newErrors[field] = err;
    });

    if (Object.values(newErrors).some(Boolean)) {
      setErrors((prev) => ({ ...prev, [id]: newErrors }));
      setMessages((prev) => ({ ...prev, [id]: 'Please fix validation errors.' }));
      return;
    }

    try {
      setLoading(true);
      const form = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        form.append(key, value);
      });

      const billFile = fileStates[id];
      if (billFile) {
        form.append('bill', billFile);
      } else if (formData.ewayBill) {
        form.append('bill', formData.ewayBill);
      }

      const res = await axios.put(`${process.env.REACT_APP_API_URI}/api/commit/update`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });

      const updated = res.data.data;

      // Reload commitments to get latest data after update
      await fetchCommitments();

      setEditingId(null);
      setMessages((prev) => ({ ...prev, [id]: 'Commitment updated successfully!' }));
    } catch (err) {
      const errorMessage = displayError(err);
      setMessages((prev) => ({ ...prev, [id]: errorMessage || 'Update failed.' }));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await axios.delete(`${process.env.REACT_APP_API_URI}/api/commit/delete`, {
        data: { commitment: { _id: id } },
        withCredentials: true,
      });

      setCommitments((prev) => prev.filter((c) => c._id !== id));
      setFormStates((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
      if (editingId === id) setEditingId(null);
      setConfirmDeleteId(null);
    } catch (err) {
      const errorMessage = displayError(err);
      setMessages((prev) => ({ ...prev, [id]: errorMessage || 'Failed to delete commitment.' }));
    } finally {
      setDeletingId(null);
    }
  };

  const renderBillPreview = (url) => {
    if (!url) return null;
    const isPDF = url.toLowerCase().endsWith('.pdf');
    return (
      <div className="my-4">
        <label className="block font-semibold mb-2 text-gray-700">Uploaded Bill:</label>
        {isPDF ? (
          <embed src={url} type="application/pdf" className="w-full max-h-[500px]" />
        ) : (
          <img src={url} alt="Uploaded Bill" className="max-w-full h-auto rounded-md" />
        )}
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <h2 className="text-4xl font-bold mb-8 text-[#3C2E00]">{user.Mauze ? user.Mauze : ''} Commitments</h2>
      {loading && <p className="text-center">Loading...</p>}

      {commitments.length === 0 && !loading && (
        <p className="text-center text-red-500">No commitments found.</p>
      )}

      <div className="space-y-6">
        {commitments.map((commit) => {
          const isEditing = editingId === commit._id;
          const form = formStates[commit._id] || {};
          const err = errors[commit._id] || {};
          const msg = messages[commit._id] || '';

          return (
            <div key={commit._id} className="border rounded-md shadow-md bg-white">
              <button
                className="w-full text-left px-6 py-4 font-semibold bg-[#f7f7f7] hover:bg-[#ececec] border-b text-[#3C2E00] flex justify-between items-center"
                onClick={() => setEditingId((prev) => (prev === commit._id ? null : commit._id))}
              >
                <span>{commit.silatItemName} ({commit.silatItemUnit}) - {commit.quantity}</span>
                <span className="text-xs font-medium capitalize text-gray-500">{commit.status}</span>
              </button>

              {isEditing && (
                <div className="p-6 space-y-6">
                  {msg && (
                    <div
                      className={`p-4 rounded-md text-center font-semibold ${
                        msg.includes('success') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {msg}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[
                      { label: 'Quantity', name: 'quantity', type: 'number', disabled: true },
                      { label: 'Contact Name', name: 'contactName', type: 'text' },
                      { label: 'Contact Email', name: 'contactEmail', type: 'email' },
                      { label: 'Contact Number', name: 'contactNumber', type: 'text' },
                      { label: 'Estimated Delivery Date', name: 'estimatedDeliveryDate', type: 'date' },
                    ].map(({ label, name, type, disabled }) => (
                      <div key={name}>
                        <label className="block font-semibold mb-1">{label}</label>
                        <input
                          type={type}
                          name={name}
                          value={form[name] || ''}
                          onChange={(e) => handleInputChange(commit._id, name, e.target.value)}
                          disabled={disabled}
                          className={`w-full border px-4 py-2 rounded-md focus:outline-none ${
                            disabled
                              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                              : 'focus:ring-2 focus:ring-[#D4A12A]'
                          }`}
                        />
                        {err[name] && <p className="text-red-600 text-sm mt-1">{err[name]}</p>}
                      </div>
                    ))}
                  </div>

                  <div>
                    <label className="block font-semibold mb-2">Upload Bill (PDF/Image):</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(commit._id, e.target.files[0])}
                      className="border p-2 rounded-md w-full"
                    />
                    {err.billFile && <p className="text-red-600 text-sm mt-1">{err.billFile}</p>}
                  </div>

                  {renderBillPreview(form.ewayBill)}

                  {commit.status === 'commited' && (
                    <div className="border-t pt-4">
                      {confirmDeleteId === commit._id ? (
                        <div className="flex items-center justify-between gap-4 bg-red-50 border border-red-200 rounded-md p-4">
                          <p className="text-sm text-red-700 font-medium">
                            Delete this commitment? This cannot be undone.
                          </p>
                          <div className="flex gap-2 shrink-0">
                            <button
                              onClick={() => handleDelete(commit._id)}
                              disabled={deletingId === commit._id}
                              className="px-4 py-1.5 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-60"
                            >
                              {deletingId === commit._id ? 'Deleting...' : 'Yes, Delete'}
                            </button>
                            <button
                              onClick={() => setConfirmDeleteId(null)}
                              className="px-4 py-1.5 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setConfirmDeleteId(commit._id)}
                          className="text-sm text-red-600 font-medium hover:underline"
                        >
                          Delete Commitment
                        </button>
                      )}
                    </div>
                  )}

                  <div className="flex justify-end space-x-4 mt-4">
                    <button
                      onClick={() => handleSubmit(commit._id)}
                      className="px-6 py-2 bg-[#D4A12A] text-white rounded hover:bg-[#b38a24]"
                    >
                      Submit
                    </button>
                    <button
                      onClick={() => {
                        setEditingId(null);
                        setFormStates((prev) => ({ ...prev, [commit._id]: commit }));
                        setMessages((prev) => ({ ...prev, [commit._id]: '' }));
                        setErrors((prev) => ({ ...prev, [commit._id]: {} }));
                      }}
                      className="px-6 py-2 bg-gray-300 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default UpdateCommitments;
